/**
 * PartyPayments — paiements (encaissements / décaissements) d'une fiche : liste, répartition, annulation d'un paiement (même reporté — doublon, erreur).
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Trash2 } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { Pill, errMsg, money, useGlBase } from "../comptabilite/_shared";
import { PARTY_CFG, PAY_CFG, POST_CLS, POST_LABEL } from "./_books";

export default function PartyPayments({ kind, party, payments, onChanged, pathFor }) {
  const base = useGlBase();
  const navigate = useNavigate();
  const cfg = PARTY_CFG[kind];
  const payCfg = PAY_CFG[cfg.payKind];
  const [busy, setBusy] = useState(null);
  const customer = kind === "customer";

  const cancel = async (p) => {
    const posted = p.post_status !== "draft";
    const msg = posted
      ? `Annuler ce ${payCfg.label.toLowerCase()} de ${money(p.amount)} du ${p.date} ?\nSon écriture sera ${p.gl_status === "posted" ? `annulée au grand livre (n° ${p.gl_no})` : "supprimée"} et les factures réparties redeviendront ${cfg.balanceLabel.toLowerCase()}.`
      : `Supprimer ce ${payCfg.label.toLowerCase()} de ${money(p.amount)} ?`;
    if (!window.confirm(msg)) return;
    setBusy(p.id);
    try {
      if (posted) await api.post(`${base}/${payCfg.path}/${p.id}/cancel`, {}); else await api.delete(`${base}/${payCfg.path}/${p.id}`);
      toast.success(`${payCfg.label} ${posted ? "annulé" : "supprimé"}`); onChanged();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(null); }
  };

  return (
    <div className="border rounded-xl overflow-hidden" data-testid="party-payments">
      <div className="px-3 py-2 bg-slate-50 border-b text-xs font-bold uppercase tracking-wide text-slate-700 flex items-center justify-between gap-2">
        <span>{payCfg.plural} ({payments?.length ?? "…"})</span>
        {pathFor && <button onClick={() => navigate(`${pathFor(customer ? "transactions-clients" : "transactions-fournisseurs", customer ? "encaissements" : "decaissements")}&party=${party.id}`)} className="font-normal normal-case text-teal-700 hover:underline inline-flex items-center gap-1" data-testid="party-payments-link">Ouvrir dans {payCfg.plural} <ArrowRight className="w-3 h-3" /></button>}
      </div>
      {payments?.length ? (
        <table className="w-full text-xs">
          <tbody className="divide-y">
            {payments.slice(0, 50).map((p) => (
              <tr key={p.id} data-testid={`party-payment-${p.id}`}>
                <td className="px-3 py-1.5 font-mono w-24">{p.date}</td>
                <td className="px-3 py-1.5">
                  {p.number && <span className="font-mono text-slate-500 mr-1">#{p.number}</span>}{p.method || payCfg.label}{p.reference ? ` · ${p.reference}` : ""}{p.legacy_source && <span className="text-slate-400"> · importé</span>}{p.payment_type === "misc" && <span className="text-amber-700"> · paiement divers (hors compte fournisseur)</span>}
                  {(p.allocations || []).length > 0 && <div className="text-[10px] text-slate-400">appliqué sur {p.allocations.map((a) => `${a.doc_number ? `#${a.doc_number}` : "pièce"} (${money(a.amount)})`).join(", ")}</div>}
                </td>
                <td className="px-3 py-1.5 w-32"><Pill cls={POST_CLS[p.post_status]}>{POST_LABEL[p.post_status]}</Pill></td>
                <td className="px-3 py-1.5 text-right font-mono tabular-nums w-24" data-testid={`party-payment-amount-${p.id}`}>{money(p.amount)}</td>
                <td className="px-2 py-1.5 w-10 text-right">
                  <button onClick={() => cancel(p)} disabled={busy === p.id} className="text-rose-500 hover:text-rose-700 p-1 disabled:opacity-50" title={p.post_status === "draft" ? "Supprimer" : "Annuler ce paiement (doublon, erreur)"} data-testid={`party-payment-cancel-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : payments && <p className="px-3 py-3 text-xs text-slate-400">Aucun paiement.</p>}
    </div>
  );
}
